import React, { FC, useMemo } from 'react'
import styles from '../TicketLanding.module.css'
import classNames from 'classnames'
import { motion, Variants } from 'framer-motion'
import { useMediaQuery } from 'react-responsive'
import { Button, LinkButton } from '@/atoms/Button'
import { MovieClapperIcon } from '@/atoms/Icons/MovieClapperIcon'
import { TicketIcon } from '@/atoms/Icons/TicketIcon'
import { Image } from '@/atoms/Image'
import { Text } from '@/atoms/Text'
import { useDisclose } from '@/hooks/useDisclose'
import { EmblaCarousel, EmblaSlide } from '@/molecules/EmblaCarousel'
import { YoutubeVideoModal } from '@/molecules/YoutubeVideoModal'
import { SweepstakeObject } from '@/types/codegen-federation'
import { useTranslate } from '@/utils/translate/translate-client'
import { useGiveawaySchedule } from '@/views/PreSales/PreSalesGiveawayView/hooks/useGiveawaySchedule'
import { ShareButton } from '@/views/TicketsLandingView/NewTicketsLandingView/components/ShareButton'

interface Props {
  sweepstakes: SweepstakeObject[]
}

const sectionVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.3,
      ease: 'easeOut',
      staggerChildren: 0.1,
    },
  },
}

const slideVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 20,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
}

const desktopSlideVariants: Variants = {
  hidden: {
    opacity: 0,
    x: 20,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.4,
      ease: 'easeOut',
    },
  },
}

export const Sweepstakes: FC<Props> = ({ sweepstakes }) => {
  const { t } = useTranslate('tickets')
  const isDesktop = useMediaQuery({ minWidth: 768 })

  return (
    <motion.div
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.2 }}
      variants={sectionVariants}
      className="relative z-10 flex flex-col gap-4 py-12 md:gap-10 md:pb-[6rem] md:pt-[5rem] xl:pb-[12.5rem] xl:pt-[7.5rem]"
    >
      <Text
        className={classNames(
          'photon-title-md md:photon-heading-lg w-full xl:photon-heading-xl',
          styles.layoutContainer,
        )}
        weight="bold"
        color="white"
      >
        {t('latestGiveaways', 'Latest Giveaways')}
      </Text>

      <EmblaCarousel
        includeArrows
        leftArrowClassname="hidden md:block"
        rightArrowClassname="hidden md:block"
        options={{
          active: false,
          breakpoints: {
            '(min-width: 768px)': {
              active: true,
              dragFree: true,
              align: 'start',
              slidesToScroll: 1,
            },
          },
        }}
        containerClassName={classNames(
          'flex flex-col gap-2 w-full md:gap-6 md:flex-row max-w-[1420px]',
          styles.layoutContainer,
        )}
      >
        {sweepstakes.map((sweepstake) => {
          return (
            <EmblaSlide fadeOutOfView className="w-full md:max-w-[650px]" key={sweepstake.id}>
              <motion.div variants={isDesktop ? desktopSlideVariants : slideVariants}>
                <SweepstakeCard sweepstake={sweepstake} />
              </motion.div>
            </EmblaSlide>
          )
        })}
      </EmblaCarousel>
    </motion.div>
  )
}

const SweepstakeCard = ({ sweepstake }: { sweepstake: SweepstakeObject }) => {
  const { t } = useTranslate('tickets')
  const { onClose, isOpen, onOpen } = useDisclose(false)
  const { title, description, squareImageUrl, slug, giveawayYoutubeId, trailerYoutubeId } = sweepstake
  const { dailyBonusEntries, isActive } = useGiveawaySchedule(slug)

  const trailerUrl = useMemo(() => {
    if (giveawayYoutubeId) return `https://www.youtube-nocookie.com/embed/${giveawayYoutubeId}`
    if (trailerYoutubeId) return `https://www.youtube-nocookie.com/embed/${trailerYoutubeId}`
    return null
  }, [giveawayYoutubeId, trailerYoutubeId])

  if (!isActive) return null

  return (
    <div className="h-full w-full rounded-xl border border-white/10 bg-orange/20 p-4 @container md:max-w-[650px] md:rounded-2xl md:p-8 lg:rounded-3xl">
      <div className="flex items-start justify-between gap-4 md:gap-6">
        <div className="space-y-2 md:space-y-3">
          {dailyBonusEntries && (
            <Text
              color="core-warning-200"
              weight="semibold"
              className="photon-caption-sm md:photon-caption-lg flex items-center gap-1"
            >
              <MovieClapperIcon color="core-warning-200" size={16} className="md:size-6" />
              {t('getBonusEntriesToday', 'Get {{bonusEntries}} bonus entries today', {
                bonusEntries: dailyBonusEntries,
              })}
            </Text>
          )}

          {title && (
            <Text color="white" weight="semibold" className="photon-title-xs md:photon-title-md block">
              {title}
            </Text>
          )}

          {description && (
            <Text color="core-gray-400" className="photon-caption-sm md:photon-caption-lg block">
              {description}
            </Text>
          )}
        </div>

        {squareImageUrl && (
          <Image alt="" src={squareImageUrl} width={75} height={75} className="rounded-xl md:size-[112px]" />
        )}
      </div>

      <div className="mt-6 flex flex-col gap-2 md:mt-6 md:flex-row">
        <LinkButton size="sm" className="grow-1 md:grow-0" href={`/giveaway/${slug}`} variant="white">
          <TicketIcon className="md:size-6" size={20} color="black" />
          {t('preOrderMovieTickets', 'Pre-Order Tickets')}
        </LinkButton>

        <div className="flex items-center gap-2 md:items-stretch">
          <Button onClick={onOpen} size="sm" className="grow-1 md:grow-0" variant="offwhite">
            {t('watchVideo', 'Watch Video')}
          </Button>

          {title && description && (
            <ShareButton
              textClassName="!block"
              buttonSize="sm"
              className="!btn-sm grow-1 md:grow-0"
              shareUrl={`https://www.angel.com/giveaway/${slug}`}
              message={description}
              title={title}
              shareIconClassName="hidden"
            />
          )}
        </div>
      </div>

      {trailerUrl && <YoutubeVideoModal handleClose={onClose} showModal={isOpen} youTubeUrl={trailerUrl} />}
    </div>
  )
}
